import { useState, useCallback, useEffect, useRef } from 'react';
import immer from 'immer';
import { merge } from 'lodash';

function useToggledState(initialValue: boolean): [boolean, () => void] {
  const [state, setState] = useState(initialValue);
  const toggleState = useCallback(() => setState((previous: any) => !previous), []);

  return [state, toggleState];
}

type FunctionType<T = any> = (data: T) => void;

export type UpdateType<T = Record<string, unknown>> = (update: FunctionType<T> | Partial<T>) => void;

const useImmer = <T>(initialState = {} as T): [T, UpdateType<T>] => {
  const [state, setState] = useState<T>(initialState);
  const updater = useCallback(
    (update: UpdateType<T>) =>
      setState((prevState) =>
        typeof update === 'function' ? immer(prevState, update as FunctionType) : merge({}, prevState, update)
      ),
    []
  );

  return [state, updater];
};

const getChangeEvent = (): string | undefined => {
  return ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange'].find((name) => `on${name}` in document);
};

const getFullscreenEnabled = (): boolean => {
  return (
    document.fullscreenEnabled !== false &&
    document.webkitFullscreenEnabled !== false &&
    document.mozFullScreenEnabled !== false &&
    !!getChangeEvent()
  );
};

const requestFullscreen = (el = document.documentElement) => {
  const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen;
  if (req) {
    req.call(el);
  }
};

const exitFullscreen = () => {
  const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen;
  if (exit) {
    exit.call(document);
  }
};

const isBrowserFullscreen = () =>
  !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.webkitIsFullScreen ||
    document.mozFullScreen
  );

const toggleFullscreen = (el: HTMLElement) => {
  if (isBrowserFullscreen()) {
    exitFullscreen();
  } else {
    requestFullscreen(el);
  }
};

// Borrowed from: https://github.com/gragland/usehooks/issues/11#issue-407479083
export default function useFullscreen() {
  const [isFullscreen, setIsFullScreen] = useState(isBrowserFullscreen());
  const changeEvent = getChangeEvent();
  const fullscreenEnabled = getFullscreenEnabled();
  useEffect(() => {
    if (fullscreenEnabled && changeEvent) {
      const handleFullscreenChange = () => setIsFullScreen(isBrowserFullscreen());
      document.addEventListener(changeEvent, handleFullscreenChange);
      return () => {
        document.removeEventListener(changeEvent, handleFullscreenChange);
      };
    }

    return undefined;
  }, [fullscreenEnabled, changeEvent]);
  return [isFullscreen, fullscreenEnabled ? toggleFullscreen : undefined];
}

export { useToggledState, useImmer, useFullscreen };

export function usePrevious(value: any) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();

  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes

  // Return previous value (happens before update in useEffect above)
  return ref.current;
}
